import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { appUserRoleOptions, assignmentRoleOptions } from '../data/options';
import { lookupLabel } from '../data/lookups';
import { supabase } from '../config/supabase';
import type { AppUserRecord, LookupState, PermissionAccess, TeamCaptainAssignmentRecord, TeamPermissionFeature, TeamPermissionPreset } from '../types';
import {
  assignmentPermissionMatrix,
  detectPermissionPreset,
  matrixForPreset,
  matrixToAssignmentPatch,
  permissionAccessLevels,
  teamPermissionFeatures,
  permissionPresetLabel,
  permissionPresetSummary,
  teamPermissionPresets,
  type TeamPermissionMatrix,
} from '../utils/permissions';
import { usePersistentScrollRestoration } from '../hooks/usePersistentViewState';

type UserManagementProps = {
  lookups: LookupState;
  refreshLookups: () => Promise<void>;
  isAdmin: boolean;
};

type ProvisioningStatusName = 'not_linked' | 'invite_pending' | 'active' | 'disabled' | 'reset_sent';

type ProvisioningStatus = {
  appUserId: string;
  accountStatus: ProvisioningStatusName;
  invitationSentAt: string | null;
  invitationAcceptedAt: string | null;
  passwordResetSentAt: string | null;
  disabledAt: string | null;
  reactivatedAt: string | null;
  lastLoginAt: string | null;
  authRecordMissing: boolean;
  emailMismatch: boolean;
};

type UserForm = {
  display_name: string;
  email: string;
  role: AppUserRecord['role'];
  linked_player_id: string;
  is_active: boolean;
};

type Snack = {
  severity: 'success' | 'error';
  message: string;
};

type TeamAccessForm = {
  app_user_id: string;
  team_id: string;
  assignment_role: TeamCaptainAssignmentRecord['assignment_role'];
  permission_preset: TeamPermissionPreset;
  notes: string;
};

type SortField = 'user' | 'workspace_role' | 'team_access' | 'status' | 'last_activity';
type SortDirection = 'asc' | 'desc';

type UserActionMenuState = {
  anchorEl: HTMLElement;
  user: AppUserRecord;
} | null;

const emptyForm: UserForm = {
  display_name: '',
  email: '',
  role: 'captain',
  linked_player_id: '',
  is_active: true,
};

const emptyTeamAccessForm: TeamAccessForm = {
  app_user_id: '',
  team_id: '',
  assignment_role: 'co_captain',
  permission_preset: 'co_captain',
  notes: '',
};

const accountStatusPresentation: Record<ProvisioningStatusName, { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }> = {
  not_linked: { label: 'Not linked to Auth user', color: 'default' },
  invite_pending: { label: 'Invite pending', color: 'warning' },
  active: { label: 'Active', color: 'success' },
  disabled: { label: 'Disabled', color: 'error' },
  reset_sent: { label: 'Reset sent', color: 'info' },
};

function validEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function formattedActivityDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function formattedActivityTime(value: string | null): string {
  return value ? new Date(value).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';
}

function userInitials(user: AppUserRecord): string {
  const parts = user.display_name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || user.email?.slice(0, 2) || '?').toUpperCase();
}

function statusRank(status: ProvisioningStatusName): number {
  if (status === 'active') return 1;
  if (status === 'invite_pending') return 2;
  if (status === 'reset_sent') return 3;
  if (status === 'disabled') return 4;
  return 5;
}

export function UserManagement({ lookups, refreshLookups, isAdmin }: UserManagementProps) {
  const auditUserId = useAuditUserId();
  const { appUserId, isOwner, canViewAnyTeamFeature, canEditAnyTeamFeature, teamFeatureAccess, readableTeamIdsForFeature } = useAuth();
  const [users, setUsers] = useState<AppUserRecord[]>([]);
  const [assignments, setAssignments] = useState<TeamCaptainAssignmentRecord[]>([]);
  const [statuses, setStatuses] = useState<Map<string, ProvisioningStatus>>(() => new Map());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProvisioningStatusName>('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('user');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AppUserRecord | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [busyUserIds, setBusyUserIds] = useState<Set<string>>(() => new Set());
  const [bulkProvisioningBusy, setBulkProvisioningBusy] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [actionMenu, setActionMenu] = useState<UserActionMenuState>(null);
  const [teamAccessOpen, setTeamAccessOpen] = useState(false);
  const [teamAccessForm, setTeamAccessForm] = useState<TeamAccessForm>(emptyTeamAccessForm);
  const [permissionAssignment, setPermissionAssignment] = useState<TeamCaptainAssignmentRecord | null>(null);
  const [permissionPreset, setPermissionPreset] = useState<TeamPermissionPreset>('custom');
  const [permissionMatrix, setPermissionMatrix] = useState<TeamPermissionMatrix>(() => matrixForPreset('viewer'));
  const [snack, setSnack] = useState<Snack | null>(null);
  const canViewUsers = isOwner || canViewAnyTeamFeature('users_permissions');
  const canEditUsers = isOwner || canEditAnyTeamFeature('users_permissions');
  const administrableTeamIds = useMemo(
    () => isOwner ? lookups.teams.map((team) => team.value) : readableTeamIdsForFeature('users_permissions'),
    [isOwner, lookups.teams, readableTeamIdsForFeature],
  );
  const administrableTeamIdSet = useMemo(() => new Set(administrableTeamIds), [administrableTeamIds]);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'user-management', ready: !loading });

  const loadUsers = useCallback(async () => {
    if (!canViewUsers) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .is('deleted_at', null)
        .order('display_name');
      if (error) throw error;
      setUsers((data || []) as AppUserRecord[]);
      const assignmentsResult = await supabase
        .from('team_captain_assignments')
        .select('*')
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('team_id');
      if (assignmentsResult.error) throw assignmentsResult.error;
      setAssignments((assignmentsResult.data || []) as TeamCaptainAssignmentRecord[]);

      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch('/api/admin/users/status', {
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      const result = await response.json() as { statuses?: ProvisioningStatus[]; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to load account status.');
      setStatuses(new Map((result.statuses || []).map((status) => [status.appUserId, status])));
    } catch (error) {
      setSnack({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Unable to load users.',
      });
    } finally {
      setLoading(false);
    }
  }, [canViewUsers]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const linkedPlayerIds = useMemo(
    () => new Set(users.filter((user) => user.linked_player_id && user.id !== editing?.id).map((user) => user.linked_player_id)),
    [editing?.id, users],
  );
  const assignmentsByUserId = useMemo(() => assignments.reduce<Record<string, TeamCaptainAssignmentRecord[]>>((acc, assignment) => {
    acc[assignment.app_user_id] = [...(acc[assignment.app_user_id] || []), assignment];
    return acc;
  }, {}), [assignments]);
  const assignedTeamIdsForAccessUser = useMemo(
    () => new Set((assignmentsByUserId[teamAccessForm.app_user_id] || []).map((assignment) => assignment.team_id)),
    [assignmentsByUserId, teamAccessForm.app_user_id],
  );
  const availableTeamAccessTeams = useMemo(
    () => lookups.teams.filter((team) => administrableTeamIdSet.has(team.value) && !assignedTeamIdsForAccessUser.has(team.value)),
    [administrableTeamIdSet, assignedTeamIdsForAccessUser, lookups.teams],
  );
  const manageableTeams = useMemo(
    () => lookups.teams.filter((team) => administrableTeamIdSet.has(team.value)),
    [administrableTeamIdSet, lookups.teams],
  );
  const userById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);

  const statusForUser = useCallback((user: AppUserRecord): ProvisioningStatusName => {
    return statuses.get(user.id)?.accountStatus || (user.is_active ? 'not_linked' : 'disabled');
  }, [statuses]);

  const lastActivityForUser = useCallback((user: AppUserRecord): string | null => {
    const status = statuses.get(user.id);
    return status?.lastLoginAt || status?.passwordResetSentAt || status?.invitationAcceptedAt || status?.invitationSentAt || status?.disabledAt || user.updated_at || user.created_at || null;
  }, [statuses]);

  const linkedPlayerLabel = useCallback((user: AppUserRecord) => {
    return user.linked_player_id ? lookupLabel(lookups, 'players', user.linked_player_id) : '';
  }, [lookups]);

  const activeAssignmentsForUser = useCallback((userId: string) => {
    return (assignmentsByUserId[userId] || []).filter((assignment) => administrableTeamIdSet.has(assignment.team_id));
  }, [administrableTeamIdSet, assignmentsByUserId]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users
      .filter((user) => {
        const userAssignments = activeAssignmentsForUser(user.id);
        if (teamFilter !== 'all' && !userAssignments.some((assignment) => assignment.team_id === teamFilter)) return false;
        if (statusFilter !== 'all' && statusForUser(user) !== statusFilter) return false;
        if (!query) return true;
        return [
          user.display_name,
          user.email || '',
          linkedPlayerLabel(user),
        ].some((value) => value.toLowerCase().includes(query));
      })
      .sort((a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;
        const teamCountA = activeAssignmentsForUser(a.id).length;
        const teamCountB = activeAssignmentsForUser(b.id).length;
        const valueA = sortField === 'workspace_role'
          ? (a.role === 'admin' || (a.role as string) === 'owner' ? 'owner' : 'user')
          : sortField === 'team_access'
            ? teamCountA
            : sortField === 'status'
              ? statusRank(statusForUser(a))
              : sortField === 'last_activity'
                ? (lastActivityForUser(a) || '')
                : a.display_name.toLowerCase();
        const valueB = sortField === 'workspace_role'
          ? (b.role === 'admin' || (b.role as string) === 'owner' ? 'owner' : 'user')
          : sortField === 'team_access'
            ? teamCountB
            : sortField === 'status'
              ? statusRank(statusForUser(b))
              : sortField === 'last_activity'
                ? (lastActivityForUser(b) || '')
                : b.display_name.toLowerCase();
        if (valueA < valueB) return -1 * direction;
        if (valueA > valueB) return 1 * direction;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [activeAssignmentsForUser, lastActivityForUser, linkedPlayerLabel, search, sortDirection, sortField, statusFilter, statusForUser, teamFilter, users]);

  const expandedUser = expandedUserId ? userById.get(expandedUserId) || null : null;
  const expandedAssignments = expandedUserId ? activeAssignmentsForUser(expandedUserId) : [];

  const requestSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const openCreate = () => {
    if (!canEditUsers) return;
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (user: AppUserRecord) => {
    if (!canEditUsers) return;
    setEditing(user);
    setForm({
      display_name: user.display_name,
      email: user.email || '',
      role: user.role,
      linked_player_id: user.linked_player_id || '',
      is_active: user.is_active,
    });
    setDialogOpen(true);
  };

  const openAddTeamAccess = (user: AppUserRecord) => {
    if (!canEditUsers || user.role === 'admin' || (user.role as string) === 'owner') return;
    const assignedTeamIds = new Set((assignmentsByUserId[user.id] || []).map((assignment) => assignment.team_id));
    const firstAvailableTeam = lookups.teams.find((team) => administrableTeamIdSet.has(team.value) && !assignedTeamIds.has(team.value));
    setTeamAccessForm({
      ...emptyTeamAccessForm,
      app_user_id: user.id,
      team_id: firstAvailableTeam?.value || '',
    });
    setTeamAccessOpen(true);
  };

  const openPermissions = (assignment: TeamCaptainAssignmentRecord) => {
    const targetUser = userById.get(assignment.app_user_id);
    if (targetUser?.role === 'admin' || (targetUser?.role as string | undefined) === 'owner') return;
    const matrix = assignmentPermissionMatrix(assignment);
    setPermissionAssignment(assignment);
    setPermissionMatrix(matrix);
    setPermissionPreset(detectPermissionPreset(matrix));
  };

  const applyPermissionPreset = (preset: TeamPermissionPreset) => {
    setPermissionPreset(preset);
    if (preset !== 'custom') setPermissionMatrix(matrixForPreset(preset));
  };

  const setFeatureAccess = (feature: TeamPermissionFeature, access: PermissionAccess) => {
    setPermissionMatrix((current) => {
      const next = { ...current, [feature]: access };
      setPermissionPreset(detectPermissionPreset(next));
      return next;
    });
  };

  const savePermissions = async () => {
    if (!permissionAssignment) return;
    if (!canEditUsers) {
      setSnack({ severity: 'error', message: 'You can view users and permissions, but cannot edit them.' });
      return;
    }
    const targetUser = userById.get(permissionAssignment.app_user_id);
    if (targetUser?.role === 'admin' || (targetUser?.role as string | undefined) === 'owner') {
      setSnack({ severity: 'error', message: 'Workspace Owner permissions cannot be changed here.' });
      return;
    }
    const preset = detectPermissionPreset(permissionMatrix);
    const { error } = await supabase
      .from('team_captain_assignments')
      .update({
        ...matrixToAssignmentPatch(permissionMatrix, preset),
        updated_by: auditUserId,
      })
      .eq('id', permissionAssignment.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setPermissionAssignment(null);
    setSnack({ severity: 'success', message: 'Team permissions updated.' });
    await loadUsers();
  };

  const saveTeamAccess = async () => {
    if (!teamAccessForm.app_user_id) return;
    if (!canEditUsers) {
      setSnack({ severity: 'error', message: 'You can view users and permissions, but cannot edit them.' });
      return;
    }
    const targetUser = userById.get(teamAccessForm.app_user_id);
    if (targetUser?.role === 'admin' || (targetUser?.role as string | undefined) === 'owner') {
      setSnack({ severity: 'error', message: 'Workspace Owner access cannot be changed here.' });
      return;
    }
    if (!teamAccessForm.team_id) {
      setSnack({ severity: 'error', message: 'Choose a team.' });
      return;
    }
    if (!administrableTeamIdSet.has(teamAccessForm.team_id)) {
      setSnack({ severity: 'error', message: 'You cannot grant access to that team.' });
      return;
    }
    if (assignedTeamIdsForAccessUser.has(teamAccessForm.team_id)) {
      setSnack({ severity: 'error', message: 'This user already has active access to that team.' });
      return;
    }
    const matrix = matrixForPreset(teamAccessForm.permission_preset);
    const { error } = await supabase.from('team_captain_assignments').insert({
      team_id: teamAccessForm.team_id,
      app_user_id: teamAccessForm.app_user_id,
      assignment_role: teamAccessForm.assignment_role,
      is_primary_captain: false,
      is_active: true,
      notes: teamAccessForm.notes.trim() || null,
      ...matrixToAssignmentPatch(matrix, teamAccessForm.permission_preset),
      created_by: auditUserId,
      updated_by: auditUserId,
    });
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setTeamAccessOpen(false);
    setSnack({ severity: 'success', message: 'Team access added.' });
    await loadUsers();
    await refreshLookups();
  };

  const saveUser = async () => {
    if (!canEditUsers) {
      setSnack({ severity: 'error', message: 'You can view users and permissions, but cannot edit them.' });
      return;
    }
    const displayName = form.display_name.trim();
    const email = form.email.trim().toLowerCase();
    if (!displayName) {
      setSnack({ severity: 'error', message: 'Display Name is required.' });
      return;
    }
    if (email && !validEmail(email)) {
      setSnack({ severity: 'error', message: 'Enter a valid email address.' });
      return;
    }
    if (editing?.linked_player_id && email !== (editing.email || '').trim().toLowerCase()) {
      setSnack({
        severity: 'error',
        message: 'A linked player’s email is managed from the Player record and verified by the account holder in Account Settings.',
      });
      return;
    }
    if (editing?.auth_user_id && form.is_active !== editing.is_active) {
      setSnack({
        severity: 'error',
        message: 'Use Disable or Reactivate so CourtCaptain and Supabase Auth remain synchronized.',
      });
      return;
    }
    if ((form.role === 'admin' || (form.role as string) === 'owner') && editing?.role !== form.role) {
      setSnack({ severity: 'error', message: 'Workspace Owner cannot be assigned from this screen.' });
      return;
    }

    const payload = {
      display_name: displayName,
      email: email || null,
      role: form.role,
      linked_player_id: form.linked_player_id || null,
      is_active: form.is_active,
      updated_by: auditUserId,
      ...(editing ? {} : { created_by: auditUserId }),
    };
    const operation = editing
      ? supabase.from('app_users').update(payload).eq('id', editing.id)
      : supabase.from('app_users').insert(payload);
    const { error } = await operation;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setDialogOpen(false);
    setSnack({ severity: 'success', message: editing ? 'User updated.' : 'User created. You can now send an invitation.' });
    await loadUsers();
    await refreshLookups();
  };

  const setBusy = (appUserId: string, busy: boolean) => {
    setBusyUserIds((current) => {
      const next = new Set(current);
      if (busy) next.add(appUserId);
      else next.delete(appUserId);
      return next;
    });
  };

  const runProvisioningAction = async (
    user: AppUserRecord,
    action: 'invite' | 'resend-invite' | 'password-reset' | 'disable' | 'reactivate',
  ) => {
    if (!canEditUsers) return;
    if ((user.role === 'admin' || (user.role as string) === 'owner') && user.id !== appUserId) {
      setSnack({ severity: 'error', message: 'Workspace Owner accounts can be changed only by the owner.' });
      return;
    }
    if (action === 'disable' && !window.confirm(`Disable login access for ${user.display_name}?`)) return;
    setActionMenu(null);
    setBusy(user.id, true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`/api/admin/users/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ appUserId: user.id }),
      });
      const result = await response.json() as { message?: string; error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to complete the requested action.');
      setSnack({ severity: 'success', message: result.message || 'User account updated.' });
      await loadUsers();
      await refreshLookups();
    } catch (error) {
      setSnack({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Unable to complete the requested action.',
      });
    } finally {
      setBusy(user.id, false);
    }
  };

  const ensureAllPlayerLoginAccounts = async () => {
    if (!canEditUsers || bulkProvisioningBusy) return;
    setBulkProvisioningBusy(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const totals = {
        checked: 0,
        authLinked: 0,
        changed: 0,
        skipped: 0,
        failed: 0,
        failures: [] as Array<{ playerName: string; error: string }>,
        total: 0,
      };
      let offset = 0;
      let hasMore = true;
      let batchCount = 0;

      while (hasMore) {
        const response = await fetch('/api/auth/ensure-all-player-accounts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ offset, limit: 5 }),
        });
        const result = await response.json() as {
          summary?: {
            checked: number;
            authLinked: number;
            changed: number;
            skipped: number;
            failed: number;
            failures: Array<{ playerName: string; error: string }>;
          };
          total?: number;
          nextOffset?: number;
          hasMore?: boolean;
          error?: string;
        };
        if (!response.ok) throw new Error(result.error || 'Unable to ensure player login accounts.');

        const summary = result.summary;
        const nextOffset = result.nextOffset;
        if (!summary || typeof nextOffset !== 'number' || !Number.isInteger(nextOffset) || nextOffset <= offset) {
          throw new Error('Player login account provisioning returned an invalid batch response.');
        }
        totals.checked += summary.checked;
        totals.authLinked += summary.authLinked;
        totals.changed += summary.changed;
        totals.skipped += summary.skipped;
        totals.failed += summary.failed;
        totals.failures.push(...(summary.failures || []));
        totals.total = result.total ?? totals.total;
        offset = nextOffset;
        hasMore = result.hasMore === true;
        batchCount += 1;
        if (batchCount > 100) throw new Error('Player login account provisioning stopped after too many batches.');
      }

      setSnack({
        severity: totals.failed ? 'error' : 'success',
        message: `Player login accounts checked: ${totals.checked}${totals.total ? ` of ${totals.total}` : ''}. Ready: ${totals.authLinked}. Updated: ${totals.changed}. Skipped: ${totals.skipped}. Failed: ${totals.failed}.${totals.failed && totals.failures[0] ? ` First issue: ${totals.failures[0].playerName} — ${totals.failures[0].error}` : ''}`,
      });
      await loadUsers();
      await refreshLookups();
    } catch (error) {
      setSnack({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Unable to ensure player login accounts.',
      });
    } finally {
      setBulkProvisioningBusy(false);
    }
  };

  const removeTeamAccess = async (assignment: TeamCaptainAssignmentRecord) => {
    if (!canEditUsers) return;
    const targetUser = userById.get(assignment.app_user_id);
    if (targetUser?.role === 'admin' || (targetUser?.role as string | undefined) === 'owner') {
      setSnack({ severity: 'error', message: 'Workspace Owner team access cannot be removed here.' });
      return;
    }
    if (!administrableTeamIdSet.has(assignment.team_id)) {
      setSnack({ severity: 'error', message: 'You cannot remove access for that team.' });
      return;
    }
    const userLabel = targetUser?.display_name || 'this user';
    const teamLabel = lookupLabel(lookups, 'teams', assignment.team_id);
    if (!window.confirm(`Remove ${userLabel}'s access to ${teamLabel}?`)) return;
    const { error } = await supabase
      .from('team_captain_assignments')
      .update({
        is_active: false,
        deleted_at: new Date().toISOString(),
        deleted_by: auditUserId,
        delete_reason: 'Removed from Application Users team access panel',
        updated_by: auditUserId,
      })
      .eq('id', assignment.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Team access removed.' });
    await loadUsers();
    await refreshLookups();
  };

  if (!canViewUsers && !isAdmin) {
    return <Alert severity="error">Users & Permissions access is required to view application users.</Alert>;
  }

  const SortHeader = ({ field, children }: { field: SortField; children: ReactNode }) => (
    <TableSortLabel active={sortField === field} direction={sortField === field ? sortDirection : 'asc'} onClick={() => requestSort(field)}>
      {children}
    </TableSortLabel>
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Application Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage workspace users, account access, and team permissions.
          </Typography>
        </Box>
        {canEditUsers && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="outlined"
              startIcon={bulkProvisioningBusy ? <CircularProgress size={16} /> : <ManageAccountsIcon />}
              onClick={ensureAllPlayerLoginAccounts}
              disabled={bulkProvisioningBusy}
            >
              Ensure Player Login Accounts
            </Button>
            <Button variant="contained" startIcon={<PersonAddAltIcon />} onClick={openCreate}>Add User</Button>
          </Stack>
        )}
      </Box>

      <Alert severity="info">
        Team permissions can be assigned here or from Team Access within each team.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { md: 280 } }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
            <MenuItem value="invite_pending">Pending / Invited</MenuItem>
            <MenuItem value="reset_sent">Reset Sent</MenuItem>
            <MenuItem value="not_linked">Not Linked</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Team</InputLabel>
          <Select label="Team" value={teamFilter} onChange={(event) => setTeamFilter(event.target.value)}>
            <MenuItem value="all">All Teams</MenuItem>
            {manageableTeams.map((team) => <MenuItem key={team.value} value={team.value}>{team.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}><SortHeader field="user">User</SortHeader></TableCell>
                <TableCell sx={{ fontWeight: 700 }}><SortHeader field="workspace_role">Workspace Role</SortHeader></TableCell>
                <TableCell sx={{ fontWeight: 700 }}><SortHeader field="team_access">Team Access</SortHeader></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Linked Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}><SortHeader field="status">Status</SortHeader></TableCell>
                <TableCell sx={{ fontWeight: 700 }}><SortHeader field="last_activity">Last Activity</SortHeader></TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => {
                const status = statuses.get(user.id);
                const statusName = statusForUser(user);
                const presentation = accountStatusPresentation[statusName];
                const busy = busyUserIds.has(user.id);
                const userAssignments = activeAssignmentsForUser(user.id);
                const isWorkspaceOwner = user.role === 'admin' || (user.role as string) === 'owner';
                const lastActivity = lastActivityForUser(user);
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, bgcolor: isWorkspaceOwner ? 'success.light' : 'grey.100', color: isWorkspaceOwner ? 'success.dark' : 'text.primary', fontSize: 14, fontWeight: 700 }}>
                          {userInitials(user)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{user.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{user.email || 'No email address'}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {isWorkspaceOwner ? (
                        <Tooltip title="Full access to all teams and workspace administration. Permissions cannot be restricted.">
                          <Chip size="small" color="success" variant="outlined" icon={<ShieldOutlinedIcon />} label="Owner" />
                        </Tooltip>
                      ) : (
                        <Chip size="small" icon={<PersonOutlineIcon />} label="User" />
                      )}
                    </TableCell>
                    <TableCell>
                      {isWorkspaceOwner ? (
                        <Typography variant="body2" color="text.secondary">All teams</Typography>
                      ) : userAssignments.length > 0 ? (
                        <Button
                          size="small"
                          endIcon={<ChevronRightIcon />}
                          onClick={() => setExpandedUserId((current) => current === user.id ? null : user.id)}
                          sx={{ px: 0.5, color: 'success.dark', fontWeight: 700 }}
                        >
                          {userAssignments.length} {userAssignments.length === 1 ? 'team' : 'teams'}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No team access</Typography>
                      )}
                    </TableCell>
                    <TableCell>{user.linked_player_id ? lookupLabel(lookups, 'players', user.linked_player_id) : '—'}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Chip size="small" label={presentation.label} color={presentation.color} />
                        {status?.authRecordMissing && <Typography variant="caption" color="error">Linked Auth record is missing</Typography>}
                        {status?.emailMismatch && <Typography variant="caption" color="warning.main">Auth email does not match</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0}>
                        <Typography variant="body2">{formattedActivityDate(lastActivity)}</Typography>
                        <Typography variant="caption" color="text.secondary">{formattedActivityTime(lastActivity)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {busy ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton size="small" aria-label={`Actions for ${user.display_name}`} onClick={(event) => setActionMenu({ anchorEl: event.currentTarget, user })}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No application users found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredUsers.length ? 1 : 0} to {filteredUsers.length} of {filteredUsers.length} users
          </Typography>
        </Box>
      </Paper>

      {expandedUser && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <ManageAccountsIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Team Access for {expandedUser.display_name}</Typography>
                <Typography variant="caption" color="text.secondary">Manage {expandedUser.display_name.split(' ')[0] || expandedUser.display_name}&apos;s roles and permissions for each team.</Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setExpandedUserId(null)} aria-label="Close team access">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role / Template</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Permission Level</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expandedAssignments.map((assignment) => {
                  const preset = detectPermissionPreset(assignmentPermissionMatrix(assignment));
                  const targetAccess = teamFeatureAccess(assignment.team_id, 'users_permissions');
                  const canManageAssignment = canEditUsers && targetAccess === 'edit' && expandedUser.role !== 'admin' && (expandedUser.role as string) !== 'owner';
                  return (
                    <TableRow key={assignment.id} hover>
                      <TableCell>{lookupLabel(lookups, 'teams', assignment.team_id)}</TableCell>
                      <TableCell><Chip size="small" label={permissionPresetLabel(preset)} /></TableCell>
                      <TableCell>{permissionPresetSummary(preset)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Button size="small" disabled={!canManageAssignment} onClick={() => openPermissions(assignment)}>Manage Permissions</Button>
                          {canManageAssignment && (
                            <IconButton size="small" color="error" aria-label="Remove team access" onClick={() => void removeTeamAccess(assignment)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {expandedAssignments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No team access.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {canEditUsers && expandedUser.role !== 'admin' && (expandedUser.role as string) !== 'owner' && (
            <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => openAddTeamAccess(expandedUser)}>Add Team Access</Button>
            </Box>
          )}
        </Paper>
      )}

      <Menu anchorEl={actionMenu?.anchorEl || null} open={Boolean(actionMenu)} onClose={() => setActionMenu(null)}>
        {actionMenu && [
          <MenuItem key="edit" disabled={!canEditUsers} onClick={() => { openEdit(actionMenu.user); setActionMenu(null); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit User
          </MenuItem>,
          <MenuItem key="access" onClick={() => { setExpandedUserId(actionMenu.user.id); setActionMenu(null); }}>
            <ManageAccountsIcon fontSize="small" sx={{ mr: 1 }} /> Manage Access
          </MenuItem>,
          statusForUser(actionMenu.user) === 'not_linked' && actionMenu.user.is_active
            ? <MenuItem key="invite" disabled={!canEditUsers || !actionMenu.user.email} onClick={() => void runProvisioningAction(actionMenu.user, 'invite')}><MailOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Invite User</MenuItem>
            : null,
          statusForUser(actionMenu.user) === 'invite_pending'
            ? <MenuItem key="resend" disabled={!canEditUsers} onClick={() => void runProvisioningAction(actionMenu.user, 'resend-invite')}><ReplayIcon fontSize="small" sx={{ mr: 1 }} /> Resend Invite</MenuItem>
            : null,
          ['active', 'reset_sent'].includes(statusForUser(actionMenu.user))
            ? <MenuItem key="reset" disabled={!canEditUsers} onClick={() => void runProvisioningAction(actionMenu.user, 'password-reset')}><LockResetIcon fontSize="small" sx={{ mr: 1 }} /> Send Password Reset</MenuItem>
            : null,
          statusForUser(actionMenu.user) === 'disabled'
            ? <MenuItem key="reactivate" disabled={!canEditUsers} onClick={() => void runProvisioningAction(actionMenu.user, 'reactivate')}><ReplayIcon fontSize="small" sx={{ mr: 1 }} /> Reactivate Account</MenuItem>
            : <MenuItem key="disable" disabled={!canEditUsers || actionMenu.user.id === appUserId} sx={{ color: 'error.main' }} onClick={() => void runProvisioningAction(actionMenu.user, 'disable')}><PersonOffIcon fontSize="small" sx={{ mr: 1 }} /> Disable Account</MenuItem>,
        ]}
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Application User' : 'Add Application User'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Display Name" required value={form.display_name} onChange={(event) => setForm((current) => ({ ...current, display_name: event.target.value }))} />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              disabled={Boolean(editing?.linked_player_id)}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              helperText={editing?.linked_player_id
                ? 'Managed from the linked Player record. The player verifies any change from Account Settings.'
                : 'Required before an invitation can be sent.'}
            />
            <FormControl fullWidth required disabled={editing?.role === 'admin' || (editing?.role as string | undefined) === 'owner'}>
              <InputLabel>Workspace Role</InputLabel>
              <Select label="Workspace Role" value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value as AppUserRecord['role'] }))}>
                {appUserRoleOptions
                  .filter((option) => option.value !== 'admin' || form.role === 'admin')
                  .map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            {(editing?.role === 'admin' || (editing?.role as string | undefined) === 'owner') && (
              <Alert severity="info">Workspace Owner has full access to all teams and workspace administration. Permissions cannot be restricted.</Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Linked Player</InputLabel>
              <Select label="Linked Player" value={form.linked_player_id} onChange={(event) => setForm((current) => ({ ...current, linked_player_id: event.target.value }))}>
                <MenuItem value="">None</MenuItem>
                {lookups.players.filter((player) => !linkedPlayerIds.has(player.value) || player.value === form.linked_player_id).map((player) => (
                  <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox disabled={Boolean(editing?.auth_user_id)} checked={form.is_active} onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))} />}
              label={editing?.auth_user_id ? 'Active status is managed with Disable / Reactivate' : 'Active CourtCaptain profile'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveUser()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={teamAccessOpen} onClose={() => setTeamAccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Access</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Granting access for {lookupLabel(lookups, 'app_users', teamAccessForm.app_user_id)}. You can fine-tune feature permissions after saving.
            </Alert>
            <FormControl fullWidth required>
              <InputLabel>Team</InputLabel>
              <Select label="Team" value={teamAccessForm.team_id} onChange={(event) => setTeamAccessForm((current) => ({ ...current, team_id: event.target.value }))}>
                {availableTeamAccessTeams.map((team) => <MenuItem key={team.value} value={team.value}>{team.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Team Role</InputLabel>
              <Select
                label="Team Role"
                value={teamAccessForm.assignment_role}
                onChange={(event) => setTeamAccessForm((current) => ({ ...current, assignment_role: event.target.value as TeamCaptainAssignmentRecord['assignment_role'] }))}
              >
                {assignmentRoleOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Permission Preset</InputLabel>
              <Select
                label="Permission Preset"
                value={teamAccessForm.permission_preset}
                onChange={(event) => setTeamAccessForm((current) => ({ ...current, permission_preset: event.target.value as TeamPermissionPreset }))}
              >
                {teamPermissionPresets.filter((preset) => preset.key !== 'custom').map((preset) => (
                  <MenuItem key={preset.key} value={preset.key}>{preset.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Notes" value={teamAccessForm.notes} multiline minRows={2} onChange={(event) => setTeamAccessForm((current) => ({ ...current, notes: event.target.value }))} />
            {availableTeamAccessTeams.length === 0 && (
              <Alert severity="warning">This user already has active access to every team.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamAccessOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveTeamAccess()} disabled={!teamAccessForm.team_id}>Add Access</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(permissionAssignment)} onClose={() => setPermissionAssignment(null)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Team Permissions</DialogTitle>
        <DialogContent>
          {permissionAssignment && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                Editing access for {lookupLabel(lookups, 'app_users', permissionAssignment.app_user_id)} on {lookupLabel(lookups, 'teams', permissionAssignment.team_id)}.
              </Alert>
              <FormControl fullWidth>
                <InputLabel>Preset</InputLabel>
                <Select label="Preset" value={permissionPreset} disabled={!canEditUsers} onChange={(event) => applyPermissionPreset(event.target.value as TeamPermissionPreset)}>
                  {teamPermissionPresets.map((preset) => (
                    <MenuItem key={preset.key} value={preset.key}>{preset.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Access</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {teamPermissionFeatures.map((feature) => (
                        <TableRow key={feature.key}>
                          <TableCell>{feature.label}</TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select value={permissionMatrix[feature.key]} disabled={!canEditUsers} onChange={(event) => setFeatureAccess(feature.key, event.target.value as PermissionAccess)}>
                                {permissionAccessLevels.filter((access) => feature.editable || access !== 'edit').map((access) => (
                                  <MenuItem key={access} value={access}>{access === 'none' ? 'Hidden' : access === 'view' ? 'View' : 'Edit'}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionAssignment(null)}>Cancel</Button>
          <Button variant="contained" disabled={!canEditUsers} onClick={() => void savePermissions()}>Save Permissions</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={6000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
